import * as THREE from 'three';
import Scene from '../scene/scene';
import { MpSdk, Sweep } from 'src/types';
import {
  getHorizontalRotationBetweenPoints,
  getVerticalRotationBetweenPoints,
} from 'src/utils/getRotation';
import { getAzimuthAngle, getSweepSid } from 'src/service/navigation/utils';
import { timeout } from 'src/utils/timeout';
import {
  CombinedSweep,
  ITargetObjectData,
  NavigationManagerProps,
  NavigationType,
  TeleportGroup,
  TeleportGroupType,
  TeleportPointComponent,
} from 'src/service/navigation/types';
import {
  makePathFinderLineComponent,
  PathFinderLineComponent,
  pathFinderLineComponentType,
} from 'src/service/navigation/component/PathFinderLine.component';
import { PathFinder } from 'src/service/navigation/PathFinder';
import iShowcaseContext from 'src/service/showcase-context-instance';
import { NodeEventsTypes } from 'src/service/scene/enums';
import React from 'react';
import { IContext } from 'src/service/showcase-context-instance/types';

export class NavigationManager extends Scene {
  private component: PathFinderLineComponent;

  public currentSID: string | null;

  public targetSID: string | null;

  public targetObjectData?: ITargetObjectData;

  public destinationTitle = 'Move to location';

  public inAutoNavigate: boolean;

  public navigationType: NavigationType = NavigationType.Shortest;

  public hasTeleports: boolean;

  public legacyIds: boolean;

  public context: IContext;

  private pathFinder: PathFinder;

  public pathCalculationSweeps: Map<string, CombinedSweep>;

  private iframe: HTMLIFrameElement;

  private currentSweep: Sweep.ObservableSweepData;

  private pathArrowRef: React.Ref<unknown>;

  private moveToSweep: (s: string, rotation: any, transition: any) => void;

  private teleportGroups: TeleportGroup[];

  private teleportPoints: TeleportPointComponent[];

  private handleTeleportClick: (
    sweep: any,
    resumeAfter: boolean,
    setStep: () => void,
  ) => Promise<void>;

  private sweepSubscription: any;

  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(mpSDK: MpSdk) {
    super(mpSDK);
  }

  public async initialize({
    iframe,
    tourId,
    pathCalculationSweeps,
    currentSweep,
    pathArrowRef,
    moveToSweep,
    teleportGroups = [],
    teleportPoints = [],
    requestFn,
    handleTeleportClick,
  }: NavigationManagerProps) {
    this.iframe = iframe;
    this.pathCalculationSweeps = pathCalculationSweeps;
    this.currentSweep = currentSweep;
    this.pathArrowRef = pathArrowRef;
    this.moveToSweep = moveToSweep;
    this.handleTeleportClick = handleTeleportClick;
    this.teleportGroups = teleportGroups;
    this.teleportPoints = teleportPoints;

    this.currentSID = getSweepSid(currentSweep, this.pathCalculationSweeps, this.legacyIds);
    this.hasTeleports = false;

    this.legacyIds = this.iframe?.src.includes('useLegacyIds=1');

    this.pathFinder = new PathFinder(tourId, requestFn);
    await this.initComponent();
  }

  async initComponent() {
    this.context = await iShowcaseContext();
    await this.mpSDK.Scene.register(pathFinderLineComponentType, makePathFinderLineComponent);
    const modelNode = await this.mpSDK.Scene.createNode();
    this.component = await modelNode.addComponent(pathFinderLineComponentType, {
      camera: this.context.camera,
      pathOffsetY: 0.15,
      arrowRef: this.pathArrowRef,
      teleportPoints: this.teleportPoints ?? [],
    });
    await this.initSweepUpdates();
    await modelNode.start();
  }

  private async initSweepUpdates() {
    // Clean up any existing subscription before creating a new one
    if (this.sweepSubscription && typeof this.sweepSubscription.unsubscribe === 'function') {
      this.sweepSubscription.unsubscribe();
    }

    this.sweepSubscription = this.mpSDK.Sweep.current.subscribe(async (currentSweep) => {
      this.currentSweep = currentSweep;
      if (currentSweep.sid || currentSweep.sweep) {
        this.currentSID = getSweepSid(currentSweep, this.pathCalculationSweeps, this.legacyIds);
        if (!this.targetSID) return;
        if (this.currentSID === this.targetSID && !this.inAutoNavigate) {
          this.reset(true);
          return;
        }
        if (this.currentSID)
          this.renderPath(
            this.currentSID,
            this.targetSID,
            this.destinationTitle,
            this.targetObjectData,
          );
      }
    });
  }

  public async rotateCameraToPathDirection() {
    const { path } = this.component.inputs;

    const currentPoint = path[0];
    const nextPoint = path[1];

    if (!currentPoint || !nextPoint) return;

    const pos1 = new THREE.Vector3(
      currentPoint.data.position.x,
      currentPoint.data.position.y,
      currentPoint.data.position.z,
    );
    const pos2 = new THREE.Vector3(
      nextPoint.data.position.x,
      nextPoint.data.position.y,
      nextPoint.data.position.z,
    );

    const yawDegrees = getHorizontalRotationBetweenPoints(pos1, pos2);
    const pitchDegrees = getVerticalRotationBetweenPoints(pos1, pos2) * 0.75;
    await this.mpSDK.Camera.setRotation({ x: pitchDegrees, y: yawDegrees }, 75);
  }

  autoNavigateCurrentPath(setStep?: any, onComplete?: any, onCancel?: any) {
    this.inAutoNavigate = true;
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      const { path } = this.component.inputs;
      // If tour is opened with startSweep queryParam, currentSweep isn't updated by SDK until user moves.
      // Uncomment once issue is fixed
      let doSweepCheck = false;
      const navigatePathStepsRecursive = async (currentPoint) => {
        const allTeleportPoints = this.teleportGroups
          ?.filter((tg) => {
            if (this.navigationType === NavigationType.NoElevators) {
              return tg.type === TeleportGroupType.Stairway;
            }
            if (this.navigationType === NavigationType.Shortest) {
              return tg.type === TeleportGroupType.Elevator;
            }
            return tg;
          })
          .map((group) => group.points)
          .flat();
        const currentIndex = path.indexOf(currentPoint);
        const nextPoint = path[currentIndex + 1];
        const sweepSet = new Set(allTeleportPoints.map((teleportPoint) => teleportPoint.sweep));
        const nexpointIsTp = sweepSet.has(nextPoint?.data?.id);
        const currentPointIsTp = sweepSet.has(currentPoint?.data?.id);

        if (!this.inAutoNavigate || (this.currentSweep?.uuid === '' && doSweepCheck)) {
          resolve(false);
          return;
        }
        doSweepCheck = true;

        // Check if we've reached the end of the path
        if (!nextPoint) {
          resolve(true);
          return;
        }

        if (
          (currentPoint === path[path.length - 1] || currentPoint === path[path.length - 2]) &&
          !currentPointIsTp &&
          !nexpointIsTp &&
          this.navigationType !== NavigationType.NoStairs
        ) {
          return;
        }

        if (nexpointIsTp && currentPointIsTp && this.navigationType !== NavigationType.NoStairs) {
          this.handleTeleport(currentPoint, nextPoint, setStep);
          return;
        }

        const pos1 = new THREE.Vector3(
          currentPoint.data.position.x,
          currentPoint.data.position.y,
          currentPoint.data.position.z,
        );
        const pos2 = new THREE.Vector3(
          nextPoint.data.position.x,
          nextPoint.data.position.y - 1.15,
          nextPoint.data.position.z,
        );

        const yawDegrees = getHorizontalRotationBetweenPoints(pos1, pos2);
        const pitchDegrees = getVerticalRotationBetweenPoints(pos1, pos2) * 0.75;

        if (!nextPoint.data.isCustom) {
          // eslint-disable-next-line no-await-in-loop
          await this.mpSDK.Camera.setRotation({ x: pitchDegrees, y: yawDegrees }, 75);
          const targetSID = this.pathCalculationSweeps.get(nextPoint.data.id)?.id;
          if (!targetSID) {
            return;
          }
          // eslint-disable-next-line no-await-in-loop
          await this.moveToSweep(targetSID, { x: pitchDegrees, y: yawDegrees }, 'FLY');
        }

        // sleep function to prevent tour crash when trying to move during automatic movement
        // eslint-disable-next-line no-await-in-loop
        await timeout(100);

        if (!this.inAutoNavigate) {
          resolve(false);
          return;
        }

        if (setStep) {
          setStep();
        }

        await navigatePathStepsRecursive(nextPoint);
      };

      try {
        await navigatePathStepsRecursive(path[0]);
        resolve(true);
      } catch (error) {
        console.error('Error in autoNavigate:', error);
        reject(error);
      }
    }).then(async (completed) => {
      if (this.inAutoNavigate && completed) {
        if (onComplete) onComplete();
        if (this.targetObjectData?.isTag && this.targetObjectData?.tag) {
          await this.mpSDK.Mattertag.navigateToTag(
            this.targetObjectData.tag.sid,
            this.mpSDK.Mattertag.Transition.FLY,
          );
        }
        const data = {
          targetObjectData: { ...this.targetObjectData },
        };
        this.reset();
        this.emit(NodeEventsTypes.NAVIGATION_COMPLETE, data);
        if (setStep) setStep(0);
      } else if (onCancel) onCancel();
    });
  }

  private handleTeleport(currentPoint: any, nextPoint: any, setStep: () => void) {
    this.inAutoNavigate = false;
    this.teleportPoints.forEach((tp) => {
      tp.pointNodes.forEach((pointNode) => {
        if (nextPoint.data.id === pointNode.sweep) {
          pointNode[0].obj3D.visible = false;
          pointNode.keepHidden = true;

          const foundInitialPointNode = tp.pointNodes.find(
            (pn) => pn.sweep === currentPoint.data.id,
          );

          const pos1 = new THREE.Vector3(
            currentPoint.data.position.x,
            this.context.camera.parent.position.y,
            currentPoint.data.position.z,
          );
          const pos2 = new THREE.Vector3(
            foundInitialPointNode[0].position.x,
            foundInitialPointNode[0].position.y,
            foundInitialPointNode[0].position.z,
          );

          const yawDegrees = getHorizontalRotationBetweenPoints(pos1, pos2);
          const pitchDegrees = getVerticalRotationBetweenPoints(pos1, pos2) * 0.5;

          this.mpSDK.Camera.setRotation({ x: pitchDegrees, y: yawDegrees }, 75).then(() => {
            setTimeout(() => {
              this.handleTeleportClick(currentPoint.data.id, true, setStep);
            }, 1000);
          });
        }
      });
    });
  }

  public async renderPath(
    currentSID: string,
    targetSID: string,
    title?: string,
    targetObjectData?: ITargetObjectData,
    navigationType?: NavigationType,
  ) {
    if (navigationType) {
      this.navigationType = navigationType;
    }
    if (!currentSID || !targetSID) {
      this.emit(NodeEventsTypes.NAVIGATION_PATH_RENDER_FAIL, {
        currentSID,
        targetSID,
        targetObjectData,
        navigationType: this.navigationType,
        error: new Error('Invalid start or end SID'),
      });
      return;
    }

    try {
      this.emit(NodeEventsTypes.NAVIGATION_PATH_RENDER_START, {
        currentSID,
        targetSID,
        targetObjectData,
        navigationType: this.navigationType,
      });

      if (title) this.destinationTitle = title;
      if (targetObjectData) this.targetObjectData = targetObjectData;

      this.currentSID = currentSID;
      this.targetSID = targetSID;

      if (!this.currentSID || !this.targetSID) return;

      const { path, hasTeleports } = await this.pathFinder.createPath(
        this.currentSID,
        this.targetSID,
        this.pathCalculationSweeps,
        targetObjectData,
        this.navigationType ?? NavigationType.Shortest,
      );

      this.hasTeleports = hasTeleports;
      this.updateTeleportPointsVisibility(hasTeleports);

      if (targetObjectData) this.component.inputs.targetObjectData = targetObjectData;
      this.component.inputs.path = path;
      this.component.inputs.navigationType = this.navigationType;

      this.emit(NodeEventsTypes.NAVIGATION_PATH_RENDER_COMPLETE, {
        path,
        targetObjectData,
      });
    } catch (error) {
      this.emit(NodeEventsTypes.NAVIGATION_PATH_RENDER_FAIL, {
        currentSID,
        targetSID,
        targetObjectData,
        navigationType: this.navigationType,
        error,
      });
    }
  }

  private updateTeleportPointsVisibility(hasTeleports: boolean) {
    if (
      // this.navigationType !== NavigationType.Shortest ||
      !hasTeleports
    ) {
      this.teleportPoints.forEach((tp) => {
        tp.pointNodes.forEach((pointNode) => {
          pointNode[0].obj3D.visible = false;
        });
      });
    } else {
      this.teleportPoints
        ?.filter((tp) =>
          this.navigationType === NavigationType.NoElevators
            ? tp.type === TeleportGroupType.Stairway
            : tp.type === TeleportGroupType.Elevator,
        )
        .forEach((tp) => {
          tp.pointNodes.forEach((pointNode) => {
            if (!pointNode.keepHidden) pointNode[0].obj3D.visible = true;
          });
        });
    }
  }

  public getAutonavigationPath() {
    const { path } = this.component.inputs;

    return path;
  }

  public getPathDirectionSigns() {
    const pathDirectionSigns: any[] = [];
    const { path } = this.component.inputs;
    const pointNodes = this.teleportPoints?.flatMap((tp) => tp.pointNodes);
    path.forEach((pathItem, index) => {
      if (!path[index - 1] || !path[index + 1] || !path[index + 2]) return;
      if (pointNodes?.find((pn) => pn?.sweep === pathItem?.data?.id)) return;
      const { angle, crossProduct } = getAzimuthAngle(
        path[index - 1].data.position,
        path[index].data.position,
        path[index + 1].data.position,
      );
      if (angle > (Math.PI * 2) / 3) return;

      let turn;
      if (crossProduct < 0) turn = 'left';
      if (crossProduct >= 0) turn = 'right';
      pathDirectionSigns.push({ ...pathItem.data, turn });
    });

    return pathDirectionSigns;
  }

  public getTeleportPoints() {
    return this.teleportPoints.filter((tp) => !!tp.pointNodes?.length);
  }

  public reset(pathFinished?: boolean) {
    this.targetSID = null;
    this.component.inputs.targetObjectData = null;
    this.component.inputs.path = [];
    this.destinationTitle = 'Move to location';
    this.targetObjectData = undefined;
    this.inAutoNavigate = false;
    this.navigationType = NavigationType.Shortest;
    this.hasTeleports = false;
    this.teleportPoints.forEach((tp) => {
      tp.pointNodes.forEach((pointNode) => {
        pointNode[0].obj3D.visible = false;
        pointNode.keepHidden = false;
      });
    });
    this.emit(NodeEventsTypes.NAVIGATION_PATH_RESET, { pathFinished });
  }

  public dispose() {
    // Clean up sweep subscription
    if (this.sweepSubscription && typeof this.sweepSubscription.unsubscribe === 'function') {
      this.sweepSubscription.unsubscribe();
      this.sweepSubscription = null;
    }
  }
}
